import React, { useEffect, useState } from 'react';
import { Timeline, Spin, message, Card } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { useGlobalContext } from '../../store/GlobalContext';
import { AuditEvent, ChangeDetails } from '../../types';
import { fetchAuditTrail } from './auditevent.service';
import styled from 'styled-components';

const TimeLineCardTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const AuditTrail: React.FC = () => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const { auth } = useGlobalContext();

  useEffect(() => {
    const loadAuditTrail = async () => {
      try {
        if (auth.accessToken) {
          const fetchedEvents = await fetchAuditTrail(auth.accessToken);
          setEvents(fetchedEvents.slice(-5));
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to load audit trail");
      } finally {
        setLoading(false);
      }
    };

    loadAuditTrail();
  }, [auth.accessToken]);

  const renderChanges = (changes: ChangeDetails) => {
    const changeItems: React.ReactNode[] = [];
    if (!changes.before || !changes.after) {
      return <p>No changes available</p>;
    }
    for (const key in changes.before) {
      // skip keys not in both before and after
      if (!(key in changes.after)) {
        continue;
      }

      // skip keys that are not changed
      if (changes.before[key] !== changes.after[key]) {
        let beforeValue = changes.before[key];
        let afterValue = changes.after[key];

        // If the value is an object, convert to string or handle appropriately
        if (typeof beforeValue === 'object' && beforeValue !== null) {
          beforeValue = JSON.stringify(beforeValue);
        }
        if (typeof afterValue === 'object' && afterValue !== null) {
          afterValue = JSON.stringify(afterValue);
        }

        changeItems.push(
          <p key={key}>
            <strong>{key}:</strong> {beforeValue} → {afterValue}
          </p>
        );
      }
    }
    return changeItems.length > 0 ? changeItems : <p>No changes available</p>;
  };

  function hasUser(event: AuditEvent): event is AuditEvent & { user: { firstName: string; accessLevel: string } } {
    return event.user !== undefined && event.user !== null;
  }

  // Convert events to Timeline items
  const timelineItems = events.map((event) => {
    const color =
      event.severity === 'high'
        ? 'red'
        : event.severity === 'medium'
        ? 'orange'
        : 'green';
    const dot =
      event.operation === 'CREATE' ? <SmileOutlined /> : undefined;
    const description = (
      <div>
        <p>
          The operation <i>{event.operation.toLowerCase()}</i> was done on the entity <strong>{event.entityName}</strong>
          {hasUser(event) ? (
            <> by <strong>{event.user.firstName}</strong> with access level <strong>{event.accessLevel}</strong> </>
          ) : (
            <> by an unknown user </>
          )}
          at <strong>{new Date(event.timestamp).toLocaleString()}</strong>.
        </p>
        {renderChanges(event.changes)}
      </div>
    );

    return {
      color: color,
      dot: dot,
      children: description,
    };
  });

  return (
    <div>
      <Card title="Audit Trail" bordered={false}>
        {loading ? (
          <Spin tip="Loading audit trail..." />
        ) : (
          <Timeline mode={"left"} items={timelineItems} />
        )}
      </Card>
    </div>
  );
};

export default AuditTrail;
